import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { QuizResults as Results, SessionSummary } from '@floorpoll/shared';
import { ExportResults } from '../../components/ExportResults.js';
import { FormError, FormNotice } from '../../components/Form.js';
import { ApiError } from '../../lib/api.js';
import { quizApi } from '../../lib/quizApi.js';
import { track } from '../../lib/track.js';
import { ResultBars } from '../../components/ResultBars.js';
import { RunHistory } from '../../components/RunHistory.js';
import { useWorkspace } from '../../lib/workspace.js';

/** Stories #26, #27 and #46 — accumulated results, clearing them, and the runs behind them. */
export function QuizResultsPage() {
  const { quizId = '' } = useParams();
  const { can } = useWorkspace();
  const [results, setResults] = useState<Results | null>(null);
  const [runs, setRuns] = useState<SessionSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    // Both at once, but settled apart. The totals are the page and the history
    // is an addition to it, so a failing history request must not be able to
    // take down story #26's results — it leaves the panel out instead, which
    // it is already conditional on.
    const [totals, sealed] = await Promise.allSettled([
      quizApi.results(quizId),
      quizApi.sessions(quizId),
    ]);

    if (totals.status === 'fulfilled') {
      track('resultsViewed');
      setResults(totals.value);
    } else {
      const caught = totals.reason;
      setError(caught instanceof ApiError ? caught.message : 'Could not load results.');
    }
    setRuns(sealed.status === 'fulfilled' ? sealed.value : []);
    setLoading(false);
  }, [quizId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function reset() {
    if (!confirm('Reset all results for this poll? Everyone will be able to vote again.')) return;
    setError(null);
    setNotice(null);

    try {
      await quizApi.resetResults(quizId);
      await load();
      setNotice('Results reset. Everyone can vote again.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not reset results.');
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (!results) {
    return (
      <section>
        <FormError message={error ?? 'Results not found.'} />
        <Link to="/polls">Back to polls</Link>
      </section>
    );
  }

  return (
    <section>
      <div className="page-head">
        <div>
          <Link to={`/polls/${quizId}`} className="muted">
            ← Back to poll
          </Link>
          <h1>{results.title}</h1>
          <p className="muted">
            {results.totalVotes} {results.totalVotes === 1 ? 'vote' : 'votes'} in total
          </p>
        </div>
        <div className="page-head-actions">
          <ExportResults
            source={{ title: results.title, runDate: null, screens: results.screens }}
          />
          {can('results:reset') ? (
            <button type="button" className="danger" onClick={() => void reset()}>
              Reset results
            </button>
          ) : null}
        </div>
      </div>

      <FormError message={error} />
      <FormNotice message={notice} />

      {runs.length > 0 ? (
        <div className="panel">
          <h2>Presentation history</h2>
          <p className="panel-caption">
            Every run, as it was that night. Resetting the totals above leaves these standing.
          </p>
          <RunHistory runs={runs} />
        </div>
      ) : null}

      {results.screens.length === 0 ? (
        <p className="muted">
          No questions yet. Add some in the editor and results will show up here.
        </p>
      ) : (
        results.screens.map((screen, index) => (
          <div className="panel" key={screen.screenId}>
            <h2>Screen {index + 1}</h2>
            <ResultBars questions={screen.questions} />
          </div>
        ))
      )}
    </section>
  );
}
